import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useLocalState } from "../../hooks";
import Api from "../../store/api";
import { verifyReq } from "../../store/slices/authSlice";
import { getInitalValues } from "../forms/profile/selector";

const normalizeUrl = (value?: string) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

type ContactProps = {
  user: any;
  editable?: boolean;
};

type ContactRow = {
  name?: string | null;
  mobileNumber?: string | null;
};

const normalizeContactNumber = (value?: string | null) => String(value || "").replace(/\D+/g, "").trim();

export default function Contact({ user, editable = false }: ContactProps) {
  const dispatch = useDispatch();
  const authUser = useSelector((state: any) => state.auth?.user);
  const profile = user?.profile || user;
  const viewedUserId = user?.id;
  const defaultState = {
    dialogOpen: false,
    saving: false,
    alt1Contact: profile?.alt1Contact || "",
    alt2Contact: profile?.alt2Contact || "",
    instagramUrl: profile?.instagramUrl || "",
    linkedinUrl: profile?.linkedinUrl || "",
    errorMessage: "",
    successOpen: false,
  };
  const [LS, setLS] = useLocalState(defaultState);
  const [mutualContacts, setMutualContacts] = useLocalState({
    loading: false,
    items: [],
  }) as [{ loading: boolean; items: ContactRow[] }, (value: any) => void];

  useEffect(() => {
    setLS({
      alt1Contact: profile?.alt1Contact || "",
      alt2Contact: profile?.alt2Contact || "",
      instagramUrl: profile?.instagramUrl || "",
      linkedinUrl: profile?.linkedinUrl || "",
    });
  }, [profile?.alt1Contact, profile?.alt2Contact, profile?.instagramUrl, profile?.linkedinUrl]);

  useEffect(() => {
    if (editable || !authUser?.id || !viewedUserId || authUser.id === viewedUserId) {
      setMutualContacts({ loading: false, items: [] });
      return;
    }

    let mounted = true;
    setMutualContacts({ loading: true, items: [] });

    Promise.all([
      Api.getContactListByUserId(authUser.id),
      Api.getContactListByUserId(viewedUserId),
    ])
      .then(([myContactsRes, viewedContactsRes]) => {
        if (!mounted) return;

        const myContacts = Array.isArray(myContactsRes?.data) ? myContactsRes.data : [];
        const viewedContacts = Array.isArray(viewedContactsRes?.data) ? viewedContactsRes.data : [];

        const viewedContactNumbers = new Set(
          viewedContacts
            .map((contact: ContactRow) => normalizeContactNumber(contact?.mobileNumber))
            .filter(Boolean)
        );

        const items = Array.from(
          myContacts
            .filter((contact: ContactRow) => {
              const number = normalizeContactNumber(contact?.mobileNumber);
              return number && viewedContactNumbers.has(number);
            })
            .reduce((map, contact: ContactRow) => {
              const number = normalizeContactNumber(contact?.mobileNumber);
              if (number && !map.has(number)) {
                map.set(number, contact);
              }
              return map;
            }, new Map<string, ContactRow>())
            .values()
        );

        setMutualContacts({ loading: false, items });
      })
      .catch(() => {
        if (!mounted) return;
        setMutualContacts({ loading: false, items: [] });
      });

    return () => {
      mounted = false;
    };
  }, [authUser?.id, viewedUserId, editable]);

  const handleChange = (field: string) => (event: any) => {
    setLS({ [field]: event.target.value });
  };

  const handleClose = () => {
    setLS({ dialogOpen: false, errorMessage: "" });
  };

  const handleSave = async () => {
    setLS({ saving: true, errorMessage: "" });
    try {
      const payload = {
        ...getInitalValues(profile),
        alt1Contact: LS.alt1Contact,
        alt2Contact: LS.alt2Contact,
        instagramUrl: LS.instagramUrl,
        linkedinUrl: LS.linkedinUrl,
      };
      const response = await Api.updatePofileDetails(payload);
      if (response.status === 200) {
        dispatch(verifyReq());
        setLS({ dialogOpen: false, saving: false, successOpen: true });
        return;
      }
      setLS({ saving: false, errorMessage: "Failed to update contact details." });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to update contact details.";
      setLS({ saving: false, errorMessage });
    }
  };

  const renderLinkValue = (label: string, value?: string) => {
    if (!value) {
      return <Typography color="textSecondary">{`${label}: -`}</Typography>;
    }

    return (
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography color="textSecondary">{`${label}:`}</Typography>
        <Button
          component="a"
          href={normalizeUrl(value)}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          {`Open ${label.replace(" URL", "")}`}
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">Contact Details</Typography>
            {editable && (
              <Button variant="outlined" size="small" onClick={() => setLS({ dialogOpen: true, errorMessage: "" })}>
                Edit
              </Button>
            )}
          </Box>
          <Divider />
          <Typography color="textSecondary">{`Alternative Contact 1: ${profile?.alt1Contact || "-"}`}</Typography>
          <Typography color="textSecondary">{`Alternative Contact 2: ${profile?.alt2Contact || "-"}`}</Typography>
          {renderLinkValue("Instagram URL", profile?.instagramUrl)}
          {renderLinkValue("LinkedIn URL", profile?.linkedinUrl)}
          {!editable && authUser?.id !== viewedUserId && (
            <Box mt={2}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Mutual Contacts
              </Typography>
              {mutualContacts.loading ? (
                <Typography color="textSecondary">Loading mutual contacts...</Typography>
              ) : mutualContacts.items.length > 0 ? (
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "64px 1.4fr 1fr",
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      backgroundColor: "rgba(0,0,0,0.04)",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>SN</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Name</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>Number</Typography>
                  </Box>
                  {mutualContacts.items.map((contact, index) => (
                    <Box
                      key={`${normalizeContactNumber(contact.mobileNumber) || index}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "64px 1.4fr 1fr",
                        gap: 1,
                        px: 1,
                        py: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">{index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {contact.name || "-"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {contact.mobileNumber || "-"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="textSecondary">No mutual contacts found.</Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={LS.dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Update Contact Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
            <TextField
              label="Alternative Contact 1"
              value={LS.alt1Contact}
              onChange={handleChange("alt1Contact")}
              fullWidth
              size="small"
            />
            <TextField
              label="Alternative Contact 2"
              value={LS.alt2Contact}
              onChange={handleChange("alt2Contact")}
              fullWidth
              size="small"
            />
            <TextField
              label="Instagram URL"
              value={LS.instagramUrl}
              onChange={handleChange("instagramUrl")}
              fullWidth
              size="small"
            />
            <TextField
              label="LinkedIn URL"
              value={LS.linkedinUrl}
              onChange={handleChange("linkedinUrl")}
              fullWidth
              size="small"
            />
            {LS.errorMessage && <Alert severity="error">{LS.errorMessage}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={LS.saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={LS.saving}>
            {LS.saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={LS.successOpen}
        autoHideDuration={3000}
        onClose={() => setLS({ successOpen: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setLS({ successOpen: false })} severity="success" sx={{ width: "100%" }}>
          Contact details updated successfully.
        </Alert>
      </Snackbar>
    </>
  );
}
